import { zodResolver } from '@hookform/resolvers/zod'
import { useKindeAuth } from '@kinde-oss/kinde-auth-react'
import { ArrowLeft, Plus } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useFieldArray, useForm } from 'react-hook-form'
import { toast } from 'react-hot-toast'
import { useNavigate, useParams } from 'react-router'
import { z } from 'zod'

import { fromCents } from '@ankor-io/common/domain/pay/currency'
import { AWXCardLimitsInterval } from '@ankor-io/common/types/awx/card'
import type { CardPayload, UpdateCardRequest } from '@ankor-io/common/types/cards'

import { useCard } from '@/hooks/useCard'
import { useUserContext } from '@/providers/user/useUserContext'
import { Button } from '@/ui-components/Button'
import { CardLimitRow } from '@/ui-components/CardLimitRow'
import { ErrorMessage, Field, Label } from '@/ui-components/Fieldset'
import { Heading } from '@/ui-components/Heading'
import { Input } from '@/ui-components/Input'
import Loading from '@/ui-components/Loading'

const limitSchema = z.object({
  id: z.string(),
  amount: z
    .number({ invalid_type_error: 'Amount is required' })
    .min(0.01, { message: 'Amount must be greater than zero' })
    .max(999999999, { message: 'Amount is too large' }),
  interval: z.nativeEnum(AWXCardLimitsInterval, { required_error: 'Interval is required' }),
})

const formSchema = z.object({
  nick_name: z
    .string()
    .optional()
    .refine((val) => !val || val.length >= 2, {
      message: 'Card nickname must be at least 2 characters',
    }),
  authorization_controls: z
    .object({
      transaction_limits: z
        .object({
          currency: z.string().optional(),
          limits: z.array(limitSchema).min(0),
        })
        .optional(),
    })
    .optional(),
})

export type CardEditFormData = z.infer<typeof formSchema>

// Component for displaying when no limits are configured
const NoLimitsMessage = () => (
  <div className="p-4 text-base/6 sm:text-sm/6 text-gray-600 dark:text-gray-400 bg-gray-50 dark:bg-gray-800 rounded-lg data-disabled:opacity-50">
    You have removed all card limits
    <br />
    Only the transaction limit will apply.
  </div>
)

export default function EditCard() {
  const { cardId } = useParams()
  const navigate = useNavigate()
  const { getAccessToken } = useKindeAuth()
  const { activeOrganizationId, activeConnectedAccountId } = useUserContext()

  const [isLoading, setIsLoading] = useState(true)
  const { setCard, getCard } = useCard()

  const form = useForm<CardEditFormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      nick_name: '',
      authorization_controls: {
        transaction_limits: {
          currency: 'AUD',
          limits: [],
        },
      },
    },
  })

  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: 'authorization_controls.transaction_limits.limits',
  })

  // Function to add a new transaction limit
  const addLimit = () => {
    append({
      id: crypto.randomUUID(),
      amount: 0,
      interval: AWXCardLimitsInterval.MONTHLY,
    })
  }

  // Fetch card data on component mount
  useEffect(() => {
    const resetFormWithCardData = (cardData: CardPayload) => {
      form.reset({
        nick_name: cardData.nickName || '',
        authorization_controls: {
          transaction_limits: {
            currency: cardData.currencyLimit || 'AUD',
            limits: cardData.limits.map((limit) => ({
              id: limit.id,
              amount: fromCents(limit.amountCents),
              interval: limit.interval,
            })),
          },
        },
      })
    }

    if (!cardId || !activeOrganizationId || !activeConnectedAccountId) {
      setIsLoading(false)
      return
    }

    // If the card in the store matches the cardId, use it and skip fetching
    const cardData = getCard(cardId)
    if (cardData) {
      resetFormWithCardData(cardData)
      setIsLoading(false)
      return
    }

    const fetchCardData = async () => {
      try {
        const response = await fetch(
          `/api/pay/org/${activeOrganizationId}/connected-account/${activeConnectedAccountId}/card/${cardId}`,
          {
            headers: {
              Authorization: `Bearer ${await getAccessToken()}`,
            },
          },
        )

        if (!response.ok) {
          throw new Error('Failed to fetch card data')
        }

        const data: CardPayload = await response.json()
        setCard(data)

        // Populate form with existing data
        resetFormWithCardData(data)
      } catch (error) {
        console.error('Error fetching card data:', error)
        toast.error('Failed to fetch card data')
      } finally {
        setIsLoading(false)
      }
    }

    fetchCardData()
  }, [cardId, activeOrganizationId, activeConnectedAccountId, getAccessToken, setCard, getCard, form])

  // Helper function to transform form limits to API format
  const transformLimitsToApiFormat = (
    limits: Array<{ id: string; amount: number; interval: AWXCardLimitsInterval }>,
  ) => {
    return limits.map((limit) => ({
      id: limit.id,
      amount: limit.amount,
      interval: limit.interval,
    }))
  }

  // Helper function to build the API payload
  const buildUpdatePayload = (values: CardEditFormData): UpdateCardRequest => {
    const basePayload: UpdateCardRequest = {
      nick_name: values.nick_name,
    }

    // Only include authorization controls if there are transaction limits
    const transactionLimits = values.authorization_controls?.transaction_limits
    const hasTransactionLimits = transactionLimits && transactionLimits.limits && transactionLimits.limits.length > 0

    if (hasTransactionLimits) {
      const { currency, limits } = transactionLimits
      basePayload.authorization_controls = {
        transaction_limits: {
          currency: currency || 'AUD',
          limits: transformLimitsToApiFormat(limits),
        },
        cash_withdrawal_limits: {
          currency: currency || 'AUD',
          limits: [],
        },
      }
    }

    return basePayload
  }

  // Helper function to update card via API
  const updateCardApi = async (payload: UpdateCardRequest) => {
    if (!cardId || !activeOrganizationId || !activeConnectedAccountId) {
      throw new Error('Missing required parameters')
    }

    const response = await fetch(
      `/api/pay/org/${activeOrganizationId}/connected-account/${activeConnectedAccountId}/card/${cardId}`,
      {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${await getAccessToken()}`,
        },
        body: JSON.stringify(payload),
      },
    )

    if (!response.ok) {
      throw new Error('Failed to update card')
    }

    const card: CardPayload = await response.json()

    setCard(card)
  }

  const onSubmit = async (values: CardEditFormData) => {
    const savePromise = async () => {
      const payload = buildUpdatePayload(values)
      await updateCardApi(payload)
    }

    await toast.promise(savePromise(), {
      loading: 'Saving card...',
      success: 'Card updated successfully',
      error: 'Failed to update card. Please try again.',
    })
  }

  if (isLoading) {
    return <Loading />
  }

  const cardData = cardId ? getCard(cardId) : null

  if (!cardData) {
    return <div>No card found</div>
  }

  return (
    <>
      <Button
        plain
        className="mb-2"
        onClick={() => navigate(-1)}
        aria-label="Go back"
      >
        <ArrowLeft data-slot="icon" />
        Back
      </Button>

      <div className="space-y-6">
        <Heading level={3}>Update Card</Heading>

        <form
          onSubmit={form.handleSubmit(onSubmit)}
          className="space-y-6"
        >
          {/* Combined Card Information and Limits Section */}
          <div className="space-y-4">
            <div className="border rounded-lg p-4 border-gray-50 dark:border-gray-800 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Field>
                  <Label htmlFor="nick_name">Card Nickname</Label>
                  <Input
                    id="nick_name"
                    placeholder="Enter card nickname"
                    {...form.register('nick_name')}
                  />
                  {form.formState.errors.nick_name && (
                    <ErrorMessage>{form.formState.errors.nick_name.message}</ErrorMessage>
                  )}
                </Field>

                <Field>
                  <Label>Currency</Label>
                  <Input
                    value={form.watch('authorization_controls.transaction_limits.currency') || 'AUD'}
                    disabled
                    className="bg-gray-100 dark:bg-gray-800"
                  />
                </Field>
              </div>

              <div className="border-t pt-4 border-gray-200 dark:border-gray-700">
                <Heading
                  level={4}
                  className="mb-2"
                >
                  Transaction Limits
                </Heading>

                {fields.map((field, index) => (
                  <CardLimitRow
                    key={field.id}
                    index={index}
                    form={form}
                    onRemove={() => remove(index)}
                    canRemove={fields.length > 1}
                  />
                ))}

                {fields.length === 0 && <NoLimitsMessage />}

                {form.formState.errors.authorization_controls?.transaction_limits?.limits && (
                  <div className="mt-3 text-base/6 sm:text-sm/6 text-red-600 dark:text-red-500 data-disabled:opacity-50">
                    {form.formState.errors.authorization_controls.transaction_limits.limits.message}
                  </div>
                )}

                <Button
                  type="button"
                  color="light"
                  onClick={addLimit}
                >
                  <Plus data-slot="icon" />
                  Additional limit
                </Button>
              </div>
            </div>
          </div>

          {/* Footer Actions */}
          <div className="flex justify-end space-x-2 pt-6">
            <Button
              type="submit"
              color="primary"
              disabled={isLoading || !form.formState.isDirty || form.formState.isSubmitting}
              className="disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {form.formState.isSubmitting ? 'Saving...' : 'Save Card'}
            </Button>
          </div>
        </form>
      </div>
    </>
  )
}
